import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotationsAPI, customersAPI, ordersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toProperCase } from '../lib/utils';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  FileOutput, Eye, Check, Send, Download, FileDown, Loader2,
  ShoppingCart, Trash2, Globe, IndianRupee, Calendar, Package,
  AlertTriangle, CheckCircle, XCircle, Copy, Mail, Edit2, ClipboardList,
  FileText, Printer, Plus, Pencil, X, RefreshCw
} from 'lucide-react';
import { STATUS_COLORS, UOM_OPTIONS, LOST_REASONS, priceBasisLabel } from '../components/quotations/constants';
import { computeLineItemTotals, formatQtyTotal, formatWeightTotal } from '../utils/lineItemTotals';
import { PdfColumnSelector } from '../components/shared/PdfColumnSelector';
import { PartSyncDiffDialog } from '../components/shared/PartSyncDiffDialog';
import { PartNoCell } from '../components/shared/PartNoCell';
import { CreateSalesOrderDialog } from '../components/quotations/CreateSalesOrderDialog';
import { DEFAULT_PROFILES } from '../config/tableColumns';

export function QuotationDetailPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [revising, setRevising] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [lostRemarks, setLostRemarks] = useState('');
  const quotationId = window.location.pathname.split('/')[2];
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    validity_days: 30,
    incoterms: '',
    payment_terms: '',
    delivery_terms: '',
    notes: '',
    handling_charge: 0
  });

  // Create Sales Order state
  const [orderDialog, setOrderDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [customerPO, setCustomerPO] = useState('');
  const [customerPODate, setCustomerPODate] = useState('');
  const [shipToAddress, setShipToAddress] = useState('');
  const [orderTaxPercentage, setOrderTaxPercentage] = useState(0);
  const [orderItems, setOrderItems] = useState([]); // Editable copy of quotation items
  const [creatingOrder, setCreatingOrder] = useState(false);

  // Send Email state
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailContext, setEmailContext] = useState(null);
  const [loadingEmailContext, setLoadingEmailContext] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to_email: '',
    from_account_id: '',
    subject: '',
    message: '',
    reply_to_email_id: null
  });

  // Line item editing state
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemData, setEditingItemData] = useState({});
  const [savingItem, setSavingItem] = useState(false);
  const [refreshingKits, setRefreshingKits] = useState(false);
  const [removingItemId, setRemovingItemId] = useState(null);
  const [addItemDialog, setAddItemDialog] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({
    part_internal_no: '',
    description: '',
    quantity: 1,
    unit_price: 0,
    uom: 'pcs',
    price_unit: 1,
    tooling_cost: 0,
    sample_cost: 0,
    production_lead_time: '',
    sample_lead_time: '',
  });

  const fetchQuotation = useCallback(async () => {
    try {
      const res = await quotationsAPI.get(quotationId);
      setQuotation(res.data);
      setEditForm({
        validity_days: res.data.validity_days || 30,
        incoterms: res.data.incoterms || '',
        payment_terms: res.data.payment_terms || '',
        delivery_terms: res.data.delivery_terms || '',
        notes: res.data.notes || '',
        handling_charge: res.data.handling_charge || 0
      });
      setSelectedItems((res.data.items || []).map(item => item.id));
      setOrderItems((res.data.items || []).map(item => ({
        ...item,
        edited_quantity: item.quantity,
        edited_unit_price: item.unit_price,
      })));
      setOrderTaxPercentage(res.data.tax_percentage || 0);
    } catch (error) {
      toast.error('Failed to load quotation');
      navigate('/quotations');
    } finally {
      setLoading(false);
    }
  }, [quotationId, navigate]);

  useEffect(() => {
    fetchQuotation();
  }, [fetchQuotation]);

  const handleCreateOrder = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    
    setCreatingOrder(true);
    try {
      // Build item overrides with edited values
      const itemOverrides = orderItems
        .filter(item => selectedItems.includes(item.id))
        .map(item => ({
          item_id: item.id,
          quantity: parseFloat(item.edited_quantity) || item.quantity,
          unit_price: parseFloat(item.edited_unit_price) || item.unit_price,
        }));

      // NOTE: tax_amount / grand_total are intentionally NOT sent — the
      // backend recomputes them from the merged items + tooling/sample/
      // handling. Sending them here was the HIGH bug in iteration 78's
      // audit (frontend excluded tooling/sample, so PDF under-billed).
      const res = await ordersAPI.create({
        quotation_id: quotationId,
        selected_item_ids: selectedItems,
        item_overrides: itemOverrides,
        customer_po_number: customerPO || null,
        customer_po_date: customerPODate || null,
        ship_to_address: shipToAddress || null,
        tax_percentage: parseFloat(orderTaxPercentage) || 0,
      });
      toast.success(`Sales Order ${res.data.order_number} created!`);
      setOrderDialog(false);
      navigate(`/orders/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleAllItems = () => {
    if (selectedItems.length === quotation?.items?.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems((quotation?.items || []).map(item => item.id));
    }
  };

  // Update editable order item field
  const updateOrderItem = (itemId, field, value) => {
    setOrderItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Sum of edited (qty × price) for currently-selected items only.
  const calculateOrderSubtotal = () => {
    return orderItems
      .filter(item => selectedItems.includes(item.id))
      .reduce((sum, item) => {
        const qty = parseFloat(item.edited_quantity) || 0;
        const price = parseFloat(item.edited_unit_price) || 0;
        const priceUnit = item.price_unit || 1;
        return sum + (qty * price / priceUnit);
      }, 0);
  };

  // Tooling + sample from the SELECTED items (server does the same on create),
  // + the quotation-level handling charge if any. Kept in one helper so the
  // preview UI can never drift from the server-side formula.
  const calculateOrderChargesBase = () => {
    const selectedTooling = orderItems
      .filter(item => selectedItems.includes(item.id))
      .reduce((sum, item) => sum + (parseFloat(item.tooling_cost) || 0), 0);
    const selectedSample = orderItems
      .filter(item => selectedItems.includes(item.id))
      .reduce((sum, item) => sum + (parseFloat(item.sample_cost) || 0), 0);
    const handling = parseFloat(quotation?.handling_charge) || 0;
    return { tooling: selectedTooling, sample: selectedSample, handling };
  };

  const calculateOrderTaxAmount = () => {
    const { tooling, sample, handling } = calculateOrderChargesBase();
    const base = calculateOrderSubtotal() + tooling + sample + handling;
    return base * (parseFloat(orderTaxPercentage) || 0) / 100;
  };

  const calculateOrderGrandTotal = () => {
    const { tooling, sample, handling } = calculateOrderChargesBase();
    return calculateOrderSubtotal() + tooling + sample + handling + calculateOrderTaxAmount();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await quotationsAPI.delete(quotationId);
      toast.success('Quotation deleted successfully');
      navigate('/quotations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quotation');
    } finally {
      setDeleting(false);
    }
  };

  const handleEdit = async () => {
    try {
      await quotationsAPI.update(quotationId, editForm);
      toast.success('Quotation updated successfully');
      setEditDialog(false);
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update quotation');
    }
  };

  // Line item editing handlers
  const startEditItem = (item) => {
    setEditingItemId(item.id);
    setEditingItemData({
      part_internal_no: item.part_internal_no || '',
      description: item.description || '',
      quantity: item.quantity || 0,
      unit_price: item.unit_price || 0,
      uom: item.uom || 'pcs',
      price_unit: item.price_unit || 1,
      tooling_cost: item.tooling_cost || 0,
      sample_cost: item.sample_cost || 0,
      production_lead_time: item.production_lead_time || '',
      sample_lead_time: item.sample_lead_time || '',
    });
  };

  const cancelEditItem = () => {
    setEditingItemId(null);
    setEditingItemData({});
  };

  const saveEditItem = async () => {
    setSavingItem(true);
    try {
      await quotationsAPI.updateItem(quotationId, editingItemId, editingItemData);
      toast.success('Item updated');
      setEditingItemId(null);
      setEditingItemData({});
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    } finally {
      setSavingItem(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (quotation.items?.length <= 1) {
      toast.error('Cannot remove the last item from a quotation');
      return;
    }
    setRemovingItemId(itemId);
    try {
      await quotationsAPI.removeItem(quotationId, itemId);
      toast.success('Item removed');
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove item');
    } finally {
      setRemovingItemId(null);
    }
  };

  const handleAddItem = async () => {
    setAddingItem(true);
    const isKgItem = newItem.uom === 'kgs';
    try {
      await quotationsAPI.addItems(quotationId, { items: [{
        is_manual: true,
        part_internal_no: newItem.part_internal_no,
        description: newItem.description,
        quantity: newItem.quantity,
        quantity_unit: isKgItem ? 'kg' : 'pcs',
        uom: newItem.uom,
        price_unit: isKgItem ? 1 : newItem.price_unit,
        unit_price_override: newItem.unit_price,
        tooling_cost: newItem.tooling_cost,
        sample_cost: newItem.sample_cost,
        production_lead_time: newItem.production_lead_time || null,
        sample_lead_time: newItem.sample_lead_time || null,
      }] });
      toast.success('Item added to quotation');
      setAddItemDialog(false);
      setNewItem({
        part_internal_no: '', description: '', quantity: 1, unit_price: 0,
        uom: 'pcs', price_unit: 1, tooling_cost: 0, sample_cost: 0,
        production_lead_time: '', sample_lead_time: '',
      });
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    } finally {
      setAddingItem(false);
    }
  };

  const downloadPdf = () => {
    setShowPrintPreview(true);
  };

  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [syncingParts, setSyncingParts] = useState(false);
  const [syncDiff, setSyncDiff] = useState(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [applyingSync, setApplyingSync] = useState(false);

  const handleRefreshFromPartMaster = async () => {
    setSyncingParts(true);
    try {
      const res = await quotationsAPI.syncParts(quotationId, true);
      const updated = res.data?.updated_count ?? 0;
      if (updated === 0) {
        toast.info('No changes detected — quotation is already in sync with Part Master.');
        return;
      }
      setSyncDiff(res.data);
      setSyncDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview Part Master sync');
    } finally {
      setSyncingParts(false);
    }
  };

  const handleConfirmPartSync = async () => {
    setApplyingSync(true);
    try {
      const res = await quotationsAPI.syncParts(quotationId, false);
      toast.success(`Refreshed ${res.data?.updated_count ?? 0} line item(s) from Part Master`);
      setSyncDialogOpen(false);
      setSyncDiff(null);
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply Part Master refresh');
    } finally {
      setApplyingSync(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setPdfDownloading(true);
      const { downloadPdfFromIframe } = await import('./print-views/shared');
      const filename = `Quotation_${quotation.quotation_number}.pdf`;
      await downloadPdfFromIframe(
        '[data-testid="quotation-preview-iframe"]',
        'landscape',
        filename,
        null,
        (success) => {
          setPdfDownloading(false);
          if (success) toast.success('PDF downloaded');
          else toast.error('PDF generation failed');
        },
        { pageNumbers: true }
      );
    } catch {
      setPdfDownloading(false);
      toast.error('Failed to generate PDF. Please try Print instead.');
    }
  };

  const handleEmailQuote = async () => {
    setLoadingEmailContext(true);
    setEmailDialog(true);
    try {
      const res = await quotationsAPI.getEmailContext(quotationId);
      const ctx = res.data;
      setEmailContext(ctx);
      setEmailForm({
        to_email: ctx.suggested_recipient || '',
        from_account_id: ctx.email_accounts?.[0]?.id || '',
        subject: ctx.suggested_subject || '',
        message: ctx.default_message || '',
        reply_to_email_id: ctx.source_email?.id || null
      });
    } catch (error) {
      toast.error('Failed to load email settings');
      setEmailDialog(false);
    } finally {
      setLoadingEmailContext(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.to_email) {
      toast.error('Recipient and sender account are required');
      return;
    }
    
    setSendingEmail(true);
    
    try {
      // Step 1: Generate PDF blob using html2pdf (same as manual download)
      const pdfBlob = await generateQuotationPdfBlob();
      
      // Task 3: Failsafe — abort if blob is missing or suspiciously small
      if (!pdfBlob || pdfBlob.size < 1024) {
        toast.error('PDF generation failed — document appears blank. Please try again.');
        setSendingEmail(false);
        return;
      }
      
      // Step 2: Create FormData with PDF attachment
      const formData = new FormData();
      formData.append('to_email', emailForm.to_email);
      if (emailForm.from_account_id) formData.append('from_account_id', emailForm.from_account_id);
      formData.append('subject', emailForm.subject);
      formData.append('message', emailForm.message || '');
      if (emailForm.reply_to_email_id) {
        formData.append('reply_to_email_id', emailForm.reply_to_email_id);
      }
      formData.append('pdf_attachment', pdfBlob, `Quotation_${quotation.quotation_number.replace('/', '-')}.pdf`);
      
      // Step 3: Send email with PDF
      await quotationsAPI.sendEmailWithPdf(quotationId, formData);
      
      toast.success('Quotation email sent successfully!', {
        description: `Sent to ${emailForm.to_email}`
      });
      setEmailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Generate PDF blob using html2pdf (same logic as Print View)
  const generateQuotationPdfBlob = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        // Load html2pdf library
        const HTML2PDF_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        
        if (!window.html2pdf) {
          const script = document.createElement('script');
          script.src = HTML2PDF_CDN;
          script.onload = async () => {
            const blob = await createPdfBlobFromQuotation();
            resolve(blob);
          };
          script.onerror = () => reject(new Error('Failed to load html2pdf'));
          document.head.appendChild(script);
        } else {
          const blob = await createPdfBlobFromQuotation();
          resolve(blob);
        }
      } catch (error) {
        reject(error);
      }
    });
  };

  // Create PDF blob from quotation data
  const createPdfBlobFromQuotation = async () => {
    // Task 1: Create a properly styled off-screen container for html2canvas
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '0';
    container.style.top = '0';
    container.style.width = '1060px';
    container.style.maxWidth = '1060px';
    container.style.padding = '10mm';
    container.style.backgroundColor = '#ffffff';
    container.style.fontFamily = 'Arial, sans-serif';
    container.style.zIndex = '-9999';
    container.style.opacity = '0';
    container.style.pointerEvents = 'none';
    container.style.overflow = 'visible';
    
    const currency = quotation.currency || 'INR';
    const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
    const symbol = symbols[currency] || currency;
    const htmlTotals = computeLineItemTotals(quotation.items || []);
    
    // Format date helper
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    
    // Build quotation HTML (matching the print view format)
    // SECURITY: content below is sanitized via DOMPurify before being assigned
    // to innerHTML. All interpolated fields are server-controlled quotation/
    // customer data already validated by FastAPI Pydantic models. Do NOT remove
    // the DOMPurify.sanitize() wrapper.
    container.innerHTML = DOMPurify.sanitize(`
      <div style="border: 1px solid #e5e7eb; padding: 20px;">
        <!-- Company Letterhead -->
        <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #1e40af; padding-bottom: 15px;">
          <h1 style="color: #1e40af; margin: 0; font-size: 24px;">SINGHANIA INTERNATIONAL LIMITED</h1>
          <p style="font-size: 11px; color: #666; margin: 5px 0;">An ISO 9001, ISO 14001, ISO 45001 and IATF 16949 certified company</p>
          <p style="font-size: 11px; color: #666; margin: 5px 0;">C-25, Phase-1, Focal Point, Dhandari Kalan, Ludhiana - 141010, Punjab, India</p>
          <p style="font-size: 10px; color: #666; margin: 5px 0;">Tel: +91 161 4611418 | Email: info@singhaniainternational.com | www.sturdfix.com</p>
        </div>
        
        <h2 style="color: #1e40af; margin-bottom: 15px; text-align: center;">QUOTATION: ${quotation.quotation_number}${quotation.revision_number > 0 ? ` (Rev ${quotation.revision_number})` : ''}</h2>
        
        <!-- Customer & Quotation Details Grid -->
        <div style="display: flex; gap: 20px; margin-bottom: 20px;">
          <!-- Customer Details -->
          <div style="flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 15px; background: #f8fafc;">
            <h4 style="margin: 0 0 10px 0; color: #1e40af; font-size: 13px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">Customer Details</h4>
            <p style="margin: 4px 0; font-size: 12px;"><strong>${quotation.customer_name || 'N/A'}</strong></p>
            ${quotation.customer_code ? `<p style="margin: 4px 0; font-size: 11px; color: #64748b;">Code: ${quotation.customer_code}</p>` : ''}
            ${quotation.customer_country ? `<p style="margin: 4px 0; font-size: 11px; color: #64748b;">Country: ${quotation.customer_country}</p>` : ''}
            ${quotation.customer_type ? `<p style="margin: 4px 0; font-size: 11px; color: ${quotation.customer_type === 'export' ? '#c2410c' : '#16a34a'};">${quotation.customer_type === 'export' ? 'Export Customer' : 'Domestic Customer'}</p>` : ''}
            ${quotation.customer_contact_person ? `<p style="margin: 4px 0; font-size: 11px; color: #64748b;">Contact: ${quotation.customer_contact_person}</p>` : ''}
          </div>
          
          <!-- Quotation Details -->
          <div style="flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 15px; background: #f8fafc;">
            <h4 style="margin: 0 0 10px 0; color: #1e40af; font-size: 13px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">Quotation Details</h4>
            <p style="margin: 4px 0; font-size: 11px;">Date: <strong>${formatDate(quotation.created_at)}</strong></p>
            <p style="margin: 4px 0; font-size: 11px;">Valid Until: <strong>${quotation.validity_date ? formatDate(quotation.validity_date) : `${quotation.validity_days || 30} days from date`}</strong></p>
            <p style="margin: 4px 0; font-size: 11px;">Currency: <strong>${currency}</strong></p>
            ${quotation.delivery_terms ? `<p style="margin: 4px 0; font-size: 11px;">Delivery: <strong>${quotation.delivery_terms}</strong></p>` : ''}
            ${quotation.payment_terms ? `<p style="margin: 4px 0; font-size: 11px;">Payment: <strong>${quotation.payment_terms}</strong></p>` : ''}
          </div>
        </div>
        
        ${quotation.inquiry_number || quotation.inquiry_date ? `
          <div style="margin-bottom: 15px; font-size: 11px; color: #475569;">
            <strong>Ref:</strong> Inquiry ${quotation.inquiry_number ? `#${quotation.inquiry_number}` : ''}
            ${quotation.inquiry_date ? ` dated ${formatDate(quotation.inquiry_date)}` : ''}
          </div>
        ` : ''}
        
        <!-- Line Items Table -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 10px;">
          <thead>
            <tr style="background: #1e40af; color: white;">
              <th style="padding: 8px; text-align: left; border: 1px solid #1e40af; width: 3%;">#</th>
              <th style="padding: 8px; text-align: left; border: 1px solid #1e40af; width: 12%;">Part No</th>
              <th style="padding: 8px; text-align: left; border: 1px solid #1e40af; width: 25%;">Description</th>
              <th style="padding: 8px; text-align: right; border: 1px solid #1e40af; width: 8%;">Qty</th>
              <th style="padding: 8px; text-align: center; border: 1px solid #1e40af; width: 6%;">UOM</th>
              <th style="padding: 8px; text-align: right; border: 1px solid #1e40af; width: 10%;">Unit Price</th>
              <th style="padding: 8px; text-align: right; border: 1px solid #1e40af; width: 10%;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${(quotation.items || []).map((item, idx) => `
              <tr style="background: ${idx % 2 === 0 ? '#ffffff' : '#f8fafc'};">
                <td style="padding: 6px; border: 1px solid #ddd;">${idx + 1}</td>
                <td style="padding: 6px; border: 1px solid #ddd; font-family: monospace; font-size: 9px;">${item.internal_part_no || item.part_id || '-'}</td>
                <td style="padding: 6px; border: 1px solid #ddd; font-size: 9px;">${item.part_description || item.description || 'N/A'}</td>
                <td style="padding: 6px; border: 1px solid #ddd; text-align: right;">${(item.quantity || 0).toLocaleString()}</td>
                <td style="padding: 6px; border: 1px solid #ddd; text-align: center;">${item.uom === 'kgs' ? 'Kg' : 'Pcs'}</td>
                <td style="padding: 6px; border: 1px solid #ddd; text-align: right;">${symbol}${(item.unit_price || 0).toFixed(4)} / ${item.uom === 'kgs' ? 'Kg' : (item.price_unit === 100 ? '100 pcs' : item.price_unit === 1000 ? '1000 pcs' : 'pc')}</td>
                <td style="padding: 6px; border: 1px solid #ddd; text-align: right; font-weight: 500;">${symbol}${(item.line_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr style="background: #f1f5f9; font-weight: 700;">
              <td style="padding: 6px; border: 1px solid #ddd;" colspan="3">TOTAL (${htmlTotals.count} item${htmlTotals.count === 1 ? '' : 's'})</td>
              <td style="padding: 6px; border: 1px solid #ddd; text-align: right;">${formatQtyTotal(htmlTotals)}</td>
              <td style="padding: 6px; border: 1px solid #ddd; text-align: center;">${formatWeightTotal(htmlTotals) === '-' ? '' : `${formatWeightTotal(htmlTotals)} Kg`}</td>
              <td style="padding: 6px; border: 1px solid #ddd;"></td>
              <td style="padding: 6px; border: 1px solid #ddd; text-align: right;">${symbol}${htmlTotals.lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          </tfoot>
        </table>
        
        <!-- Totals Section -->
        <div style="text-align: right; margin-bottom: 20px; padding: 15px; background: #f8fafc; border-radius: 8px;">
          <p style="margin: 4px 0; font-size: 12px;"><strong>Subtotal:</strong> ${symbol}${(quotation.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          ${quotation.tooling_total > 0 ? `<p style="margin: 4px 0; font-size: 12px;">Tooling: ${symbol}${quotation.tooling_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>` : ''}
          ${quotation.sample_total > 0 ? `<p style="margin: 4px 0; font-size: 12px;">Sample: ${symbol}${quotation.sample_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>` : ''}
          ${quotation.handling_charge > 0 ? `<p style="margin: 4px 0; font-size: 12px;">Handling: ${symbol}${quotation.handling_charge.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>` : ''}
          ${quotation.tax_amount > 0 ? `<p style="margin: 4px 0; font-size: 12px;">Tax (${quotation.tax_percentage || 0}%): ${symbol}${quotation.tax_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>` : ''}
          ${quotation.discount_amount > 0 ? `<p style="margin: 4px 0; font-size: 12px; color: #16a34a;">Discount: -${symbol}${quotation.discount_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>` : ''}
          <p style="margin: 8px 0 0 0; font-size: 16px; color: #1e40af;"><strong>TOTAL: ${symbol}${(quotation.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></p>
        </div>
        
        <!-- Terms & Conditions -->
        ${quotation.terms_conditions ? `
          <div style="margin-top: 20px; padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px;">
            <h4 style="margin: 0 0 10px 0; color: #1e40af; font-size: 13px;">Terms & Conditions</h4>
            <p style="font-size: 10px; white-space: pre-wrap; color: #475569;">${quotation.terms_conditions}</p>
          </div>
        ` : ''}
        
        <!-- Footer -->
        <div style="margin-top: 30px; padding-top: 15px; border-top: 2px solid #1e40af; text-align: center; font-size: 10px; color: #666;">
          <p style="margin: 2px 0;">This is a computer generated quotation. No signature required.</p>
          <p style="margin: 2px 0;">For queries: info@singhaniainternational.com | +91 161 4611418</p>
          <p style="margin: 2px 0;">www.sturdfix.com | www.singhaniainternational.com</p>
        </div>
      </div>
    `, { ADD_TAGS: ['style'], ADD_ATTR: ['style'] });
    
    document.body.appendChild(container);
    
    try {
      // Task 2: Wait for browser to fully render the container before capture
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 150);
          });
        });
      });

      const options = {
        margin: [10, 14, 10, 14],
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: 1060,
          onclone: (clonedDoc, clonedEl) => {
            clonedDoc.querySelectorAll('.no-print, .download-pdf-btn, [data-no-print]').forEach(el => {
              el.style.display = 'none';
            });
            clonedEl.style.width = '1060px';
            clonedEl.style.maxWidth = '1060px';
            clonedEl.style.overflow = 'visible';
            clonedEl.style.boxSizing = 'border-box';
            clonedEl.querySelectorAll('table').forEach(t => {
              t.style.width = '100%';
              t.style.maxWidth = '100%';
              t.style.tableLayout = 'fixed';
              t.style.overflow = 'visible';
            });
          },
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4',
          orientation: 'landscape'
        }
      };
      
      // Generate PDF blob
      const pdfBlob = await window.html2pdf()
        .set(options)
        .from(container)
        .output('blob');
      
      document.body.removeChild(container);

      // Task 3: Validate blob is not blank/empty
      if (!pdfBlob || pdfBlob.size < 1024) {
        throw new Error(`PDF generation failed: blob size ${pdfBlob?.size || 0} bytes is too small`);
      }

      return pdfBlob;
    } catch (error) {
      document.body.removeChild(container);
      throw error;
    }
  };

  const handleRevise = async () => {
    try {
      setRevising(true);
      const res = await quotationsAPI.revise(quotationId);
      toast.success(`Revision ${res.data.quotation?.quotation_number || ''} created!`);
      // Navigate to the new revision
      navigate(`/quotations/${res.data.quotation?.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create revision');
    } finally {
      setRevising(false);
    }
  };

  const updateStatus = async () => {
    try {
      await quotationsAPI.updateStatus(quotationId, newStatus, lostReason, lostRemarks);
      toast.success(`Status updated to ${newStatus}`);
      setStatusDialog(false);
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const formatCurrency = (amount) => {
    const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
    const currency = quotation?.currency || 'INR';
    return `${symbols[currency] || currency}${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const lineTotals = computeLineItemTotals(quotation?.items || []);

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  if (!quotation) return null;

  const isDraft = quotation.status === 'draft' || quotation.status === 'pending_approval';
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  // Financial edits (add/remove/update items, convert to order) are now
  // manager-or-admin only on the backend (SEC-002). Keep the UI in sync so
  // user-role staff don't see edit buttons that silently 403 on click.
  const canEditFinancials = isManager || isAdmin;
  const canEdit = (isDraft || isAdmin) && canEditFinancials;

  const staleKitLines = (quotation.items || []).filter((item) => item.bom_changed);

  const refreshKitPrices = async () => {
    setRefreshingKits(true);
    try {
      const response = await quotationsAPI.refreshKitPrices(quotationId);
      toast.success(response.data.message);
      await fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not refresh the kit prices');
    } finally { setRefreshingKits(false); }
  };
  const canDelete = isDraft || isAdmin;

  const previewUrl = `/quotations/${quotationId}/print?preview=true`;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Quotation PDF Preview Modal */}
      <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
        <DialogContent className="max-w-[900px] w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="quotation-preview-modal">
          <DialogHeader className="px-6 pt-4 pb-2 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Quotation Preview
            </DialogTitle>
            <DialogDescription>
              {quotation.quotation_number} — {quotation.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden">
            <iframe
              src={previewUrl}
              title="Quotation Print Preview"
              className="w-full h-full border-0"
              data-testid="quotation-preview-iframe"
            />
          </div>
          <DialogFooter className="px-6 py-3 border-t shrink-0 flex flex-row items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPrintPreview(false)}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const iframe = document.querySelector('[data-testid="quotation-preview-iframe"]');
                if (iframe?.contentWindow) iframe.contentWindow.print();
              }}
            >
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
            <Button onClick={handleDownloadPdf} disabled={pdfDownloading} data-testid="quotation-download-pdf-btn">
              {pdfDownloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {pdfDownloading ? 'Generating...' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">{quotation.quotation_number}</h1>
          <p className="text-muted-foreground">{quotation.customer_name} • {quotation.customer_code}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/quotations')}>Back</Button>
          
          {/* Edit Button - For drafts or admin */}
          {canEdit && (
            <Dialog open={editDialog} onOpenChange={setEditDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="edit-quotation-btn">
                  <Edit2 className="mr-2 h-4 w-4" />
                  Edit
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Edit Quotation</DialogTitle>
                  <DialogDescription>
                    Update quotation details for {quotation.quotation_number}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Validity (Days)</Label>
                      <Input
                        type="number"
                        value={editForm.validity_days}
                        onChange={(e) => setEditForm({...editForm, validity_days: parseInt(e.target.value) || 30})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Handling Charge</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editForm.handling_charge}
                        onChange={(e) => setEditForm({...editForm, handling_charge: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Incoterms</Label>
                    <Select 
                      value={editForm.incoterms} 
                      onValueChange={(v) => setEditForm({...editForm, incoterms: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Select incoterms" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EXW">EXW - Ex Works</SelectItem>
                        <SelectItem value="FOB">FOB - Free on Board</SelectItem>
                        <SelectItem value="CIF">CIF - Cost, Insurance & Freight</SelectItem>
                        <SelectItem value="DDP">DDP - Delivered Duty Paid</SelectItem>
                        <SelectItem value="FCA">FCA - Free Carrier</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Terms</Label>
                    <Input
                      value={editForm.payment_terms}
                      onChange={(e) => setEditForm({...editForm, payment_terms: e.target.value})}
                      onBlur={(e) => e.target.value && setEditForm(prev => ({...prev, payment_terms: toProperCase(e.target.value)}))}
                      placeholder="e.g., 50% Advance, 50% on delivery"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Delivery Terms</Label>
                    <Input
                      value={editForm.delivery_terms}
                      onChange={(e) => setEditForm({...editForm, delivery_terms: e.target.value})}
                      onBlur={(e) => e.target.value && setEditForm(prev => ({...prev, delivery_terms: toProperCase(e.target.value)}))}
                      placeholder="e.g., 4-6 weeks from order"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                      placeholder="Additional notes or remarks"
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
                  <Button className="btn-primary" onClick={handleEdit}>Save Changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          <Button variant="outline" onClick={handleRevise} disabled={revising}>
            {revising ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Copy className="mr-2 h-4 w-4" />}
            Create Revision
          </Button>
          <Button variant="outline" onClick={handleRefreshFromPartMaster}
            disabled={syncingParts}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="refresh-from-part-master-btn"
            title="Pull latest description / part number / HS code etc. from Part Master into this quotation">
            {syncingParts ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh from Part Master
          </Button>
          <Button variant="outline" onClick={downloadPdf} data-testid="view-quotation-pdf-btn">
            <Eye className="mr-2 h-4 w-4" />
            View / Download PDF
          </Button>
          <Button 
            variant="outline" 
            onClick={handleEmailQuote}
            className="border-blue-200 text-blue-700 hover:bg-blue-50"
            data-testid="send-email-btn"
          >
            <Send className="mr-2 h-4 w-4" />
            Send via Email
          </Button>
          
          {/* Create Sales Order Button - only for won quotes or non-lost/cancelled statuses */}
          {quotation.status && !['lost', 'cancelled', 'expired'].includes(quotation.status) && (
            <Button 
              variant="outline"
              onClick={() => setOrderDialog(true)}
              className="border-green-200 text-green-700 hover:bg-green-50"
              data-testid="create-order-btn"
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              Create Sales Order
            </Button>
          )}
          
          {/* Delete Button with confirmation */}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  disabled={deleting}
                  data-testid="delete-quotation-btn"
                >
                  {deleting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Quotation</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete <strong>{quotation.quotation_number}</strong>?
                    <br /><br />
                    {!isDraft && (
                      <span className="text-amber-600 font-medium">
                        ⚠️ This quotation has status &quot;{quotation.status}&quot;. Deleting it may affect records.
                      </span>
                    )}
                    <br />
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-quotation-btn"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary">Update Status</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Update Quotation Status</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>New Status</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open"><div className="flex items-center gap-2"><Send className="h-4 w-4" />Mark as Open (Sent to Customer)</div></SelectItem>
                      <SelectItem value="won"><div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" />Mark as Won</div></SelectItem>
                      <SelectItem value="lost"><div className="flex items-center gap-2"><XCircle className="h-4 w-4 text-red-600" />Mark as Lost</div></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newStatus === 'lost' && (
                  <>
                    <div className="space-y-2">
                      <Label>Lost Reason *</Label>
                      <Select value={lostReason} onValueChange={setLostReason}>
                        <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                        <SelectContent>
                          {LOST_REASONS.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Remarks</Label>
                      <Textarea value={lostRemarks} onChange={(e) => setLostRemarks(e.target.value)} placeholder="Additional details..." />
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStatusDialog(false)}>Cancel</Button>
                <Button className="btn-primary" onClick={updateStatus} disabled={!newStatus || (newStatus === 'lost' && !lostReason)}>
                  Update Status
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Create Sales Order Dialog */}
          <CreateSalesOrderDialog
            open={orderDialog}
            onOpenChange={setOrderDialog}
            quotation={quotation}
            customerPO={customerPO} setCustomerPO={setCustomerPO}
            customerPODate={customerPODate} setCustomerPODate={setCustomerPODate}
            orderTaxPercentage={orderTaxPercentage} setOrderTaxPercentage={setOrderTaxPercentage}
            shipToAddress={shipToAddress} setShipToAddress={setShipToAddress}
            orderItems={orderItems} updateOrderItem={updateOrderItem}
            selectedItems={selectedItems}
            toggleItemSelection={toggleItemSelection}
            toggleAllItems={toggleAllItems}
            formatCurrency={formatCurrency}
            calculateOrderSubtotal={calculateOrderSubtotal}
            calculateOrderTaxAmount={calculateOrderTaxAmount}
            calculateOrderGrandTotal={calculateOrderGrandTotal}
            calculateOrderChargesBase={calculateOrderChargesBase}
            handleCreateOrder={handleCreateOrder}
            creatingOrder={creatingOrder}
          />

          {/* Send Email Dialog */}
          <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-blue-600" />
                  Send Quotation via Email
                </DialogTitle>
                <DialogDescription>
                  Send {quotation?.quotation_number} directly to the customer with PDF attached
                </DialogDescription>
              </DialogHeader>
              
              {loadingEmailContext ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-4 py-4">
                  {/* Threading indicator */}
                  {emailContext?.has_source_email && (
                    <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-md border border-blue-100">
                      <Mail className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-700">
                        This email will appear as a reply to the original RFQ thread
                      </span>
                    </div>
                  )}
                  
                  {/* To Email */}
                  <div className="space-y-2">
                    <Label htmlFor="to_email">To (Recipient)</Label>
                    <Input
                      id="to_email"
                      type="email"
                      placeholder="customer@example.com"
                      value={emailForm.to_email}
                      onChange={(e) => setEmailForm({...emailForm, to_email: e.target.value})}
                      data-testid="email-to-input"
                    />
                  </div>
                  
                  {/* From Account */}
                  <div className="space-y-2">
                    <Label>From (Send As)</Label>
                    {emailContext?.email_accounts?.length > 0 ? (
                      <Select 
                        value={emailForm.from_account_id} 
                        onValueChange={(v) => setEmailForm({...emailForm, from_account_id: v})}
                      >
                        <SelectTrigger data-testid="email-from-select">
                          <SelectValue placeholder="Select sender account" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailContext.email_accounts.map((acc) => (
                            <SelectItem key={acc.id} value={acc.id}>
                              {acc.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-800" data-testid="email-company-sender-note">
                        Sending from the company address (info@singhaniainternational.com) — replies come back to the same mailbox. Add a personal mailbox under Admin → Email Settings if you want to send as yourself.
                      </div>
                    )}
                  </div>
                  
                  {/* Subject */}
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={emailForm.subject}
                      onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                      data-testid="email-subject-input"
                    />
                  </div>
                  
                  {/* Message Body */}
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      rows={6}
                      value={emailForm.message}
                      onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                      data-testid="email-message-input"
                    />
                  </div>
                  
                  {/* Attachment indicator */}
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md">
                    <FileText className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-muted-foreground">
                      PDF attachment: <strong>Quotation_{quotation?.quotation_number}.pdf</strong> (auto-generated)
                    </span>
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setEmailDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !emailForm.to_email || loadingEmailContext}
                  data-testid="send-email-btn-confirm"
                >
                  {sendingEmail ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating PDF & Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send Email
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center gap-4">
        <Badge className={`${STATUS_COLORS[quotation.status]} text-sm px-3 py-1`}>
          {quotation.status?.toUpperCase()}
        </Badge>
        {quotation.revision_number > 0 && (
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
            Revision #{quotation.revision_number}
          </Badge>
        )}
        {quotation.parent_quote_id && (
          <span className="text-sm text-muted-foreground">
            (Based on original quote)
          </span>
        )}
        {quotation.lost_reason && (
          <span className="text-sm text-red-600">
            Lost Reason: {LOST_REASONS.find(r => r.value === quotation.lost_reason)?.label}
          </span>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Currency</p>
            <p className="text-2xl font-bold">{quotation.currency}</p>
            {quotation.exchange_rate !== 1 && (
              <p className="text-xs text-muted-foreground">Rate: {quotation.exchange_rate}</p>
            )}
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Line Items</p>
            <p className="text-2xl font-bold">{quotation.items?.length || 0}</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Validity</p>
            <p className="text-2xl font-bold">{quotation.validity_days} days</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard bg-primary/5">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Amount</p>
            <p className="text-2xl font-bold font-mono text-primary">{formatCurrency(quotation.total_amount)}</p>
          </CardContent>
        </Card>
      </div>

      {/* PDF Table Columns Selector */}
      <div className="mb-4">
        <PdfColumnSelector
          selectedColumns={quotation.pdf_columns || DEFAULT_PROFILES.quotation}
          autoHideEmpty={quotation.auto_hide_empty !== false}
          docType="quotation"
          onChange={async (cols, autoHide) => {
            try {
              await quotationsAPI.update(quotationId, { pdf_columns: cols, auto_hide_empty: autoHide });
              setQuotation(prev => ({ ...prev, pdf_columns: cols, auto_hide_empty: autoHide }));
              toast.success('PDF columns updated');
            } catch { toast.error('Failed to update columns'); }
          }}
        />
      </div>

      {/* Line Items */}
      <Card className="card-dashboard">
        {staleKitLines.length > 0 && (
          <div className="mx-6 mt-6 flex flex-col gap-2 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between"
            data-testid="kit-bom-changed-banner">
            <span>
              {staleKitLines.length} kit line(s) have a newer BOM price. Nothing was changed automatically.
            </span>
            {canEdit && (
              <Button size="sm" onClick={refreshKitPrices} disabled={refreshingKits} data-testid="refresh-kit-prices-btn">
                {refreshingKits ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Refresh kit prices from BOM
              </Button>
            )}
          </div>
        )}
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-heading uppercase">Line Items</CardTitle>
          {canEdit && (
            <Dialog open={addItemDialog} onOpenChange={setAddItemDialog}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="add-item-btn">
                  <Plus className="mr-1 h-4 w-4" /> Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Line Item</DialogTitle>
                  <DialogDescription>Add a new part to this quotation</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Part Number</Label>
                    <Input value={newItem.part_internal_no} onChange={(e) => setNewItem({...newItem, part_internal_no: e.target.value})} placeholder="e.g. SI-1234" data-testid="new-item-part-no" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Description</Label>
                    <Input value={newItem.description} onChange={(e) => setNewItem({...newItem, description: e.target.value})} placeholder="Part description" data-testid="new-item-description" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{newItem.uom === 'kgs' ? 'Quantity (Kg)' : 'Quantity (Pcs)'}</Label>
                    <Input type="number" value={newItem.quantity} onChange={(e) => setNewItem({...newItem, quantity: parseFloat(e.target.value) || 0})} data-testid="new-item-qty" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{newItem.uom === 'kgs' ? 'Rate per Kg' : 'Unit Price'}</Label>
                    <Input type="number" step="0.00001" value={newItem.unit_price} onChange={(e) => setNewItem({...newItem, unit_price: parseFloat(e.target.value) || 0})} data-testid="new-item-price" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">UOM</Label>
                    <Select value={newItem.uom} onValueChange={(v) => setNewItem({...newItem, uom: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {UOM_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {newItem.uom !== 'kgs' && (
                    <div className="space-y-1">
                      <Label className="text-xs">Price Per</Label>
                      <Select value={String(newItem.price_unit)} onValueChange={(v) => setNewItem({...newItem, price_unit: parseInt(v)})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 pc</SelectItem>
                          <SelectItem value="100">100 pcs</SelectItem>
                          <SelectItem value="1000">1000 pcs</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">Tooling Cost</Label>
                    <Input type="number" step="0.01" value={newItem.tooling_cost} onChange={(e) => setNewItem({...newItem, tooling_cost: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Sample Cost</Label>
                    <Input type="number" step="0.01" value={newItem.sample_cost} onChange={(e) => setNewItem({...newItem, sample_cost: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Production Lead Time</Label>
                    <Input value={newItem.production_lead_time} onChange={(e) => setNewItem({...newItem, production_lead_time: e.target.value})} placeholder="e.g. 4-6 weeks" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Sample Lead Time</Label>
                    <Input value={newItem.sample_lead_time} onChange={(e) => setNewItem({...newItem, sample_lead_time: e.target.value})} placeholder="e.g. 2 weeks" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddItemDialog(false)}>Cancel</Button>
                  <Button onClick={handleAddItem} disabled={addingItem || !newItem.description} data-testid="confirm-add-item-btn">
                    {addingItem ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
                    Add Item
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          <div className="table-wrapper overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Part No</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Costing #</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>UOM</TableHead>
                  <TableHead>Unit Price</TableHead>
                  <TableHead>Per</TableHead>
                  <TableHead>Lead Time</TableHead>
                  <TableHead>Tooling</TableHead>
                  <TableHead>Sample</TableHead>
                  <TableHead>Line Total</TableHead>
                  {canEdit && <TableHead className="w-24">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotation.items?.map((item) => (
                  <TableRow key={item.id} className="table-row">
                    {editingItemId === item.id ? (
                      <>
                        <TableCell>
                          <Input className="h-8 text-sm w-28 font-mono" value={editingItemData.part_internal_no}
                            onChange={(e) => setEditingItemData({...editingItemData, part_internal_no: e.target.value})}
                            data-testid={`edit-item-partno-${item.id}`} />
                        </TableCell>
                        <TableCell>
                          <Input className="h-8 text-sm w-40" value={editingItemData.description}
                            onChange={(e) => setEditingItemData({...editingItemData, description: e.target.value})}
                            data-testid={`edit-item-desc-${item.id}`} />
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{item.costing_number || '-'}</TableCell>
                        <TableCell>
                          <Input type="number" className="h-8 text-sm w-20 font-mono" value={editingItemData.quantity}
                            onChange={(e) => setEditingItemData({...editingItemData, quantity: parseFloat(e.target.value) || 0})}
                            data-testid={`edit-item-qty-${item.id}`} />
                        </TableCell>
                        <TableCell>
                          <Select value={editingItemData.uom} onValueChange={(v) => setEditingItemData({...editingItemData, uom: v})}>
                            <SelectTrigger className="h-8 text-xs w-20"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {UOM_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.00001" className="h-8 text-sm w-24 font-mono" value={editingItemData.unit_price}
                            onChange={(e) => setEditingItemData({...editingItemData, unit_price: parseFloat(e.target.value) || 0})}
                            data-testid={`edit-item-price-${item.id}`} />
                        </TableCell>
                        <TableCell>
                          <Select value={String(editingItemData.price_unit)} onValueChange={(v) => setEditingItemData({...editingItemData, price_unit: parseInt(v)})}>
                            <SelectTrigger className="h-8 text-xs w-16"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">/1</SelectItem>
                              <SelectItem value="100">/100</SelectItem>
                              <SelectItem value="1000">/1000</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <Input className="h-7 text-xs w-24" value={editingItemData.production_lead_time} placeholder="Prod"
                              onChange={(e) => setEditingItemData({...editingItemData, production_lead_time: e.target.value})} />
                            <Input className="h-7 text-xs w-24" value={editingItemData.sample_lead_time} placeholder="Sample"
                              onChange={(e) => setEditingItemData({...editingItemData, sample_lead_time: e.target.value})} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.01" className="h-8 text-sm w-20 font-mono" value={editingItemData.tooling_cost}
                            onChange={(e) => setEditingItemData({...editingItemData, tooling_cost: parseFloat(e.target.value) || 0})} />
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.01" className="h-8 text-sm w-20 font-mono" value={editingItemData.sample_cost}
                            onChange={(e) => setEditingItemData({...editingItemData, sample_cost: parseFloat(e.target.value) || 0})} />
                        </TableCell>
                        <TableCell className="font-mono font-medium text-muted-foreground">
                          {formatCurrency((editingItemData.quantity || 0) * (editingItemData.unit_price || 0) / (editingItemData.price_unit || 1))}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={saveEditItem} disabled={savingItem} data-testid={`save-item-${item.id}`}>
                              {savingItem ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={cancelEditItem} data-testid={`cancel-edit-item-${item.id}`}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="font-medium"><PartNoCell customerPartNo={item.customer_part_no} internalPartNo={item.part_internal_no} /></TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {item.bom_id ? (
                            <div>
                              <span data-testid={`kit-line-bom-${item.id}`}>From BOM V{item.bom_version_number}</span>
                              {item.bom_changed && (
                                <span className="block text-amber-700" data-testid={`kit-line-bom-changed-${item.id}`}>
                                  BOM changed{item.bom_current_kit_cost != null ? ` \u2192 \u20b9${Number(item.bom_current_kit_cost).toFixed(4)}` : ''}
                                </span>
                              )}
                            </div>
                          ) : (item.costing_number || '-')}
                        </TableCell>
                        <TableCell className="font-mono">{item.quantity?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {item.uom === 'kgs' ? 'Kg' : item.uom === 'per_100' ? 'Pcs' : item.uom === 'per_1000' ? 'Pcs' : 'Pcs'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs font-mono">
                            /{priceBasisLabel(item)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {item.production_lead_time && <div>Prod: {item.production_lead_time}</div>}
                          {item.sample_lead_time && <div>Sample: {item.sample_lead_time}</div>}
                        </TableCell>
                        <TableCell className="font-mono">{item.tooling_cost > 0 ? formatCurrency(item.tooling_cost) : '-'}</TableCell>
                        <TableCell className="font-mono">{item.sample_cost > 0 ? formatCurrency(item.sample_cost) : '-'}</TableCell>
                        <TableCell className="font-mono font-medium">{formatCurrency(item.line_total)}</TableCell>
                        {canEdit && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEditItem(item)} data-testid={`edit-item-${item.id}`}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" disabled={removingItemId === item.id || quotation.items?.length <= 1} data-testid={`remove-item-${item.id}`}>
                                    {removingItemId === item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remove Item</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Remove &quot;{item.description}&quot; from this quotation? This cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleRemoveItem(item.id)}>Remove</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        )}
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow className="bg-muted/60 font-semibold">
                  <TableCell colSpan={3}>TOTAL ({lineTotals.count} item{lineTotals.count === 1 ? '' : 's'})</TableCell>
                  <TableCell className="font-mono" data-testid="quotation-items-total-qty">
                    {formatQtyTotal(lineTotals)}
                    {formatWeightTotal(lineTotals) !== '-' && (
                      <span className="block text-xs font-normal text-muted-foreground" data-testid="quotation-items-total-weight">
                        {formatWeightTotal(lineTotals)} Kg total wt
                      </span>
                    )}
                  </TableCell>
                  <TableCell colSpan={4} />
                  <TableCell className="font-mono">{lineTotals.toolingCost ? formatCurrency(lineTotals.toolingCost) : '-'}</TableCell>
                  <TableCell className="font-mono">{lineTotals.sampleCost ? formatCurrency(lineTotals.sampleCost) : '-'}</TableCell>
                  <TableCell className="font-mono" data-testid="quotation-items-total-amount">{formatCurrency(lineTotals.lineTotal)}</TableCell>
                  {canEdit && <TableCell />}
                </TableRow>
              </TableFooter>
            </Table>
          </div>
          {/* Totals Summary */}
          <div className="flex justify-end mt-4 pt-4 border-t">
            <div className="w-72 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal:</span><span className="font-mono">{formatCurrency(quotation.subtotal)}</span></div>
              {quotation.tooling_total > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Tooling:</span><span className="font-mono">{formatCurrency(quotation.tooling_total)}</span></div>}
              {quotation.sample_total > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Samples:</span><span className="font-mono">{formatCurrency(quotation.sample_total)}</span></div>}
              {quotation.handling_charge > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Handling:</span><span className="font-mono">{formatCurrency(quotation.handling_charge)}</span></div>}
              {quotation.tax_amount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Tax:</span><span className="font-mono">{formatCurrency(quotation.tax_amount)}</span></div>}
              {quotation.discount_amount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount:</span><span className="font-mono text-green-600">-{formatCurrency(quotation.discount_amount)}</span></div>}
              <div className="flex justify-between font-bold text-base border-t pt-2"><span>Total:</span><span className="font-mono text-primary">{formatCurrency(quotation.total_amount)}</span></div>
            </div>
          </div>
        </CardContent>
      </Card>

      <PartSyncDiffDialog
        open={syncDialogOpen}
        onOpenChange={(v) => { setSyncDialogOpen(v); if (!v) setSyncDiff(null); }}
        changes={syncDiff?.changes || []}
        onConfirm={handleConfirmPartSync}
        applying={applyingSync}
        docLabel="quotation"
      />
    </div>
  );
}

